import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import ParticleBackground from '@/components/ParticleBackground';
import { useSiteContent } from '@/hooks/use-site-content';

const heroImage = '/images/rakib-hero.webp';

const HeroSection = () => {
  const { getText, getJson } = useSiteContent('home');

  const typingTextsData = getJson<{ texts: string[] }>('hero_typing_texts', { texts: ['SEO Expert', 'Digital Marketer'] });
  const texts = typingTextsData.texts || ['SEO Expert', 'Digital Marketer'];

  const greeting = getText('hero_greeting', "Hello, I'm");
  const heroName = getText('hero_name', 'Rakib Hossen Topu');
  const heroDescription = getText('hero_description', 'As a leading SEO Expert in BD and Digital Marketing Specialist in Bangladesh, I drive business growth through strategic search engine optimization. Specializing in Local SEO, E-commerce SEO, YouTube SEO, Technical SEO, and Meta Ads campaigns that deliver measurable results and maximize ROI for businesses across Bangladesh and worldwide.');
  const buttonText = getText('hero_button_text', 'View Portfolio');
  const imageAlt = getText('hero_image_alt', 'Rakib Hossen Topu - Professional SEO Expert in BD');

  const [textIndex, setTextIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentText = texts[textIndex];
    if (!currentText) return;
    const typingSpeed = isDeleting ? 50 : 100;
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        if (displayedText.length < currentText.length) {
          setDisplayedText(currentText.slice(0, displayedText.length + 1));
        } else {
          setTimeout(() => setIsDeleting(true), 2000);
        }
      } else {
        if (displayedText.length > 0) {
          setDisplayedText(currentText.slice(0, displayedText.length - 1));
        } else {
          setIsDeleting(false);
          setTextIndex(prev => (prev + 1) % texts.length);
        }
      }
    }, typingSpeed);
    return () => clearTimeout(timeout);
  }, [displayedText, isDeleting, textIndex, texts]);

  return (
    <section id="home" className="min-h-screen flex items-center pt-20 pb-12 relative" aria-label="Hero section - Rakib Hossen Topu SEO Expert in BD">
      <ParticleBackground />
      <div className="container mx-auto px-6 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <article className="space-y-6" itemScope itemType="https://schema.org/Person">
            <p className="text-sm uppercase tracking-wider text-muted-foreground font-semibold">
              {greeting}
            </p>
            <h1 className="text-5xl lg:text-7xl font-bold leading-tight my-0 md:text-6xl" itemProp="name">
              {heroName}
              <span className="sr-only"> - SEO Expert in BD | Best Digital Marketing Specialist Bangladesh | Search Engine Optimization Expert</span>
            </h1>
            <meta itemProp="jobTitle" content="SEO Expert in BD | Digital Marketing Specialist" />
            <meta itemProp="email" content="rakibhassantopu@gmail.com" />
            <meta itemProp="telephone" content="+8801862772430" />
            <meta itemProp="url" content="https://rakibhossentopu.online" />
            <div className="h-20 flex items-center">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold" itemProp="description">
                A <span className="gradient-text">{displayedText}<span className="animate-pulse">|</span></span>
              </h2>
            </div>
            <p className="text-lg text-muted-foreground max-w-xl" dangerouslySetInnerHTML={{ __html: heroDescription }} />
            <div className="flex flex-wrap gap-4 pt-4">
              <Button variant="hero" size="lg" className="button-press group" onClick={(e) => {
                e.preventDefault();
                const element = document.getElementById('portfolio');
                if (element) {
                  const offsetTop = element.offsetTop - 80;
                  window.scrollTo({ top: offsetTop, behavior: 'smooth' });
                }
              }} aria-label="View SEO Portfolio of Rakib Hossen Topu">
                {buttonText}
                <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Button>
            </div>
          </article>

          {/* Image */}
          <figure className="relative flex items-end justify-center" aria-label="Rakib Hossen Topu Profile Image">
            <div className="absolute inset-0 flex items-center justify-center -translate-y-8" aria-hidden="true">
              <div 
                className="absolute w-[95%] h-[95%] bg-primary/20 animate-float"
                style={{
                  borderRadius: '60% 40% 55% 45% / 55% 60% 40% 45%',
                  animationDelay: '0s',
                  willChange: 'transform'
                }}
              />
              <div 
                className="absolute w-[85%] h-[85%] bg-primary-glow/20 animate-float"
                style={{
                  borderRadius: '55% 45% 60% 40% / 40% 55% 45% 60%',
                  animationDelay: '1s',
                  willChange: 'transform'
                }}
              />
            </div>
            <div className="relative z-10 w-full max-w-[400px] lg:max-w-[480px]">
              <div className="absolute inset-0 bg-primary/30 blur-3xl rounded-full scale-75 -translate-y-4" aria-hidden="true" />
              <img 
                src={heroImage} 
                alt={imageAlt}
                title="Rakib Hossen Topu - SEO Expert in Bangladesh"
                width={480}
                height={600}
                className="relative w-full h-auto object-contain drop-shadow-[0_0_40px_rgba(254,1,104,0.3)]"
                style={{ clipPath: 'inset(0 0 8% 0)' }}
                fetchPriority="high"
                loading="eager"
                decoding="async"
                itemProp="image"
              />
              <figcaption className="sr-only">Rakib Hossen Topu - Leading SEO Expert in Bangladesh providing professional SEO and digital marketing services</figcaption>
            </div>
          </figure>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
